import { useEffect, useState } from "react";
import { CheckCircle2, XCircle, Wifi, ChevronDown, ChevronUp, Info } from "lucide-react";
import api from "@/lib/api";

/**
 * Real-time ESP32 device connectivity indicator.
 *
 * Polls GET /api/projects/{projectId}/device-status every 5 seconds and renders:
 *   - LIVE (green + check) when a real ESP32 pushed within the last 30s
 *   - OFFLINE (red + X) when no push OR stale > 30s
 * When OFFLINE, an expandable card lists context-aware troubleshooting steps.
 */
export default function DeviceStatusIndicator({ projectId }) {
  const [status, setStatus] = useState(null);
  const [expanded, setExpanded] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [_clockTick, setClockTick] = useState(0); // force re-render for elapsed counter

  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    const fetchStatus = async () => {
      try {
        const { data } = await api.get(`/projects/${projectId}/device-status`);
        if (!cancelled) setStatus(data);
      } catch {
        if (!cancelled) setStatus(null);
      }
    };
    fetchStatus();
    const pollId = setInterval(fetchStatus, 5000);
    const clockId = setInterval(() => setClockTick((c) => c + 1), 1000);
    return () => {
      cancelled = true;
      clearInterval(pollId);
      clearInterval(clockId);
    };
  }, [projectId]);

  if (!projectId) return null;

  const online = status?.online === true;
  const secondsSince = status?.seconds_since;
  const lastSeenText = status?.last_seen_at
    ? new Date(status.last_seen_at).toLocaleString([], {
        month: "short",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      })
    : null;
  const reason = status?.reason;

  // Context-aware troubleshooting recommendations
  const recommendations = (() => {
    if (reason === "no_data_ever") {
      return [
        { icon: <Wifi className="w-4 h-4" />, text: "Periksa koneksi Wi-Fi ESP32 — pastikan SSID & password benar." },
        { icon: <Info className="w-4 h-4" />, text: "Pastikan device key & endpoint backend di sketch sesuai (ESP32 Wizard → tab Device Key)." },
        { icon: <CheckCircle2 className="w-4 h-4" />, text: "Coba restart ESP32 atau periksa power supply (min 5V 1A stabil)." },
        { icon: <CheckCircle2 className="w-4 h-4" />, text: "Verifikasi apakah backend menerima data via POST /api/readings (cek log server)." },
      ];
    }
    // stale (was live before but > 30s)
    return [
      { icon: <Wifi className="w-4 h-4" />, text: "Cek sinyal Wi-Fi lokasi — kemungkinan koneksi terputus." },
      { icon: <Info className="w-4 h-4" />, text: "Pastikan endpoint backend masih dapat diakses dari ESP32 (firewall/DNS)." },
      { icon: <CheckCircle2 className="w-4 h-4" />, text: "Restart ESP32; cek power supply — brownout menyebabkan device reboot." },
      { icon: <CheckCircle2 className="w-4 h-4" />, text: "Cek log backend apakah request POST /api/readings masih diterima." },
    ];
  })();

  return (
    <div className="flex flex-col items-end gap-1" data-testid="device-status-indicator">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-label={online ? "ESP32 online" : "ESP32 offline"}
        data-testid="device-status-toggle"
        data-online={online}
        className={`flex items-center gap-2 px-2.5 py-1 rounded-md border text-xs font-medium transition-colors ${
          online
            ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400"
            : "border-red-500/40 bg-red-500/10 text-red-700 dark:text-red-400"
        }`}
      >
        {online ? <CheckCircle2 className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />}
        <span className="tracking-wider uppercase" data-testid="device-status-label">
          {online ? "Live" : "Offline"}
        </span>
        {online && secondsSince !== null && secondsSince !== undefined && (
          <span className="opacity-70 normal-case tracking-normal" data-testid="device-status-age">
            · {Math.floor(secondsSince)}s
          </span>
        )}
        {!online && (
          <>
            {expanded ? <ChevronUp className="w-3 h-3 opacity-70" /> : <ChevronDown className="w-3 h-3 opacity-70" />}
          </>
        )}
      </button>

      {!online && expanded && (
        <div
          className="w-72 sm:w-80 mt-1 border border-red-500/30 bg-card rounded-md shadow-sm p-3 space-y-2 text-left"
          data-testid="device-status-recommendations"
        >
          <div className="text-xs text-muted-foreground">
            {reason === "no_data_ever" ? (
              <>Belum pernah menerima data dari ESP32 untuk proyek ini.</>
            ) : (
              <>
                Terakhir aktif:{" "}
                <span className="text-foreground font-medium">{lastSeenText}</span>
                {secondsSince !== null && secondsSince !== undefined && (
                  <> · {Math.floor(secondsSince)} detik lalu</>
                )}
              </>
            )}
          </div>
          {status?.simulator_active && (
            <div className="text-[11px] text-amber-600 dark:text-amber-400 bg-amber-500/10 border border-amber-500/30 rounded px-2 py-1">
              Simulator aktif — data grafik saat ini dari simulator, bukan ESP32 fisik.
            </div>
          )}
          <div className="overline text-muted-foreground text-[10px]">Langkah rekomendasi</div>
          <ol className="space-y-1.5 text-xs">
            {recommendations.map((rec, idx) => (
              <li key={idx} className="flex items-start gap-2" data-testid={`device-status-rec-${idx}`}>
                <span className="mt-0.5 text-red-500 shrink-0">{rec.icon}</span>
                <span className="flex-1">
                  <span className="text-muted-foreground mr-1">{idx + 1}.</span>
                  {rec.text}
                </span>
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
}
